<script setup lang="ts">
// Japan fund page UI Task 12 (spec 2026-09-28-japan-fund-page-ui): the L&G
// Japan Index Signal tab. JAPAN strikes a BUY/SELL decision at 08:40 London
// off the NIY (Nikkei 225 future) move since the Tokyo close. `ctx` is
// accepted (uniform signalTab contract) and ignored — this tab polls its
// own data.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type JapanSignal } from '../../api/client'
import type { SignalTabCtx } from '../nav/fundPages'
import NiyMoveCard from '../japan/NiyMoveCard.vue'
import JapanLiveSynthNavCard from '../japan/JapanLiveSynthNavCard.vue'
import { useJapanLiveSynthNav } from '../../composables/useLiveSynthNav'
import { describeJapanReason } from '../japan/japanReasons'
import { useLondonDecisionClock } from '../../composables/useLondonDecisionClock'
import SignalVerdict from '../signal/SignalVerdict.vue'
import NavSourceCard from '../signal/NavSourceCard.vue'
import DisabledTabBody from './DisabledTabBody.vue'

defineProps<{ ctx: SignalTabCtx }>()

const SIGNAL_POLL_MS = 60_000
const NAV_POLL_MS = 10 * 60_000     // the NAV lands once a day — no need to hammer it

const signal = ref<JapanSignal | null>(null)
// Spec 2026-10-04: live synthetic NAV tile (5 s poll).
const { data: liveSynth, error: liveSynthError } = useJapanLiveSynthNav()
const navLatest = ref<{ date: string; navPence: number } | null>(null)

// London 08:40 decision clock, UK holiday calendar and the derived
// awaiting/missed/no-trade states — shared with CharterisSignalTab
// (extracted 2026-10-07, Charteris Phase 2.5 Task 5 fix round 1).
const {
  todayLondon, pastDecisionTime, hasSignal, lateState, awaiting, noTrade,
  lastDecisionLabel, decidedAtLabel, countdown, businessDaysBetween,
} = useLondonDecisionClock(signal)

const decisionSubtitle = computed(() => {
  if (!hasSignal.value) return 'No Japan signal yet'
  if (lateState.value === 'missed') return 'No Japan decision published today — check the signal service'
  if (lateState.value === 'unconfirmed') return 'No decision today (holiday or missed publish)'
  if (awaiting.value) {
    return pastDecisionTime.value
      ? '08:40 decision running late — not yet published'
      : 'Awaiting 08:40 decision'
  }
  return describeJapanReason(signal.value!).text
})

const statePill = computed(() => {
  if (lateState.value === 'missed') return { state: 'degraded' as const, label: 'Missed publish' }
  if (lateState.value === 'unconfirmed') return { state: 'degraded' as const, label: 'Unconfirmed' }
  if (hasSignal.value && signal.value!.degraded) return { state: 'degraded' as const, label: 'Degraded' }
  return null
})

const rawSignal = computed(() => {
  const s = signal.value
  if (!hasSignal.value || lateState.value || awaiting.value || s!.m_0840 == null || !s!.threshold_bps) return null
  return (s!.m_0840 * 10000) / s!.threshold_bps
})

const verdictProps = computed(() => {
  // A missed/unconfirmed day must not present yesterday's decision as today's.
  // Nor may an awaited one: until today's decision is published the headline
  // stays blank (the last decision is on the "Last decision" line, dated).
  const s = hasSignal.value && !lateState.value && !awaiting.value ? signal.value! : null
  return {
    fund: 'JAPAN' as const,
    decision: (s?.decision?.toLowerCase() ?? null) as 'buy' | 'sell' | null,
    decisionLabel: s?.decision ?? '—',
    decisionSubtitle: decisionSubtitle.value,
    statePill: statePill.value,
    rawSignal: rawSignal.value,
    needleScale: 2,
    showNavBlock: false,
    cutoffLabel: 'Decision',
    provisional: awaiting.value,
    synthNavPence: null,
    overnightReturnPct: null,
    anchorPence: null,
    anchorDateLabel: null,
    xdBanner: null,
    countdown: countdown.value,
    noTrade: noTrade.value,
  }
})

const staleDays = computed(() =>
  navLatest.value ? businessDaysBetween(navLatest.value.date, todayLondon.value) : null)

// --- polling -----------------------------------------------------------------
async function pollSignal() {
  try {
    signal.value = await api.japanSignal()
  } catch {
    signal.value = null
  }
}
async function pollNav() {
  try {
    const r = await api.japanNavLatest()
    navLatest.value = r && typeof r.date === 'string' && Number.isFinite(r.navPence) ? r : null
  } catch {
    navLatest.value = null
  }
}

let signalTimer: ReturnType<typeof setInterval> | null = null
let navTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  pollSignal()
  pollNav()
  signalTimer = setInterval(pollSignal, SIGNAL_POLL_MS)
  navTimer = setInterval(pollNav, NAV_POLL_MS)
})
onUnmounted(() => {
  if (signalTimer) clearInterval(signalTimer)
  if (navTimer) clearInterval(navTimer)
})
</script>

<template>
  <div class="card japan-verdict">
    <SignalVerdict v-bind="verdictProps" />
    <div v-if="lastDecisionLabel" class="japan-last-decision" data-testid="japan-last-decision">{{ lastDecisionLabel }}</div>
    <div v-if="decidedAtLabel" class="japan-last-decision" data-testid="japan-decided-at">{{ decidedAtLabel }}</div>
  </div>
  <NiyMoveCard v-if="hasSignal" :signal="signal!" :today-london="todayLondon" />
  <JapanLiveSynthNavCard :data="liveSynth" :fetch-failed="liveSynthError != null" />
  <NavSourceCard
    source="published"
    :kt-return-pct="null" :basket-return-pct="null" :anchor-pence="null" :anchor-date-label="null"
    :published-date="navLatest?.date ?? null"
    :published-nav-pence="navLatest?.navPence ?? null"
    :stale-days="staleDays" />
  <DisabledTabBody reason="Holdings not tracked for Japan (index fund)" />
</template>

<style scoped>
.japan-last-decision { font-size: 0.78rem; color: #8b949e; margin-top: -6px; }
</style>
